/**
 * Full-Screen Presentation Controller
 * Handles slide navigation, full-screen mode, and keyboard shortcuts
 */

class PresentationController {
  constructor() {
    this.currentSlideIndex = 0;
    this.slides = [];
    this.fullscreenContainer = null;
    this.isFullscreen = false;

    this.init();
  }

  init() {
    // Find all slide elements and extract their data
    const slideElements = document.querySelectorAll('[data-slide]');
    slideElements.forEach((element, index) => {
      const slideData = {
        number: parseInt(element.dataset.slide),
        contentType: element.dataset.contentType,
        title: element.dataset.title,
        subtitle: element.dataset.subtitle,
        content: element.dataset.content ? JSON.parse(element.dataset.content) : null,
        element: element
      };
      this.slides.push(slideData);
    });

    // Create full-screen container
    this.createFullscreenContainer();

    // Bind event listeners
    this.bindEvents();
  }

  createFullscreenContainer() {
    // Create the full-screen container element
    const container = document.createElement('div');
    container.id = 'fullscreen-presentation';
    container.className = 'fixed inset-0 bg-gray-900 hidden z-50';
    container.innerHTML = `
      <div id="slide-container" class="w-full h-full relative">
        <!-- Slides will be rendered here -->
      </div>

      <!-- Navigation Controls -->
      <div class="fixed bottom-6 right-6 flex items-center gap-3 z-10">
        <!-- Slide Counter -->
        <div class="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg">
          <span class="text-sm font-medium text-gray-700">
            <span id="current-slide-number">1</span> / <span id="total-slides">${this.slides.length}</span>
          </span>
        </div>

        <!-- Previous Button -->
        <button id="prev-slide-btn" class="bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg hover:bg-white transition-colors cursor-pointer group">
          <svg class="w-5 h-5 text-gray-700 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
        </button>

        <!-- Next Button -->
        <button id="next-slide-btn" class="bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg hover:bg-white transition-colors cursor-pointer group">
          <svg class="w-5 h-5 text-gray-700 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>

        <!-- Exit Button -->
        <button id="exit-fullscreen-btn" class="bg-red-500/90 backdrop-blur-sm p-3 rounded-lg shadow-lg hover:bg-red-600 transition-colors cursor-pointer group">
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;

    document.body.appendChild(container);
    this.fullscreenContainer = container;
  }

  bindEvents() {
    // Full-screen trigger button
    const fullscreenBtn = document.getElementById('enter-fullscreen-btn');
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', () => this.enterFullscreen());
    }

    // Navigation buttons
    const prevBtn = document.getElementById('prev-slide-btn');
    const nextBtn = document.getElementById('next-slide-btn');
    const exitBtn = document.getElementById('exit-fullscreen-btn');

    if (prevBtn) prevBtn.addEventListener('click', () => this.previousSlide());
    if (nextBtn) nextBtn.addEventListener('click', () => this.nextSlide());
    if (exitBtn) exitBtn.addEventListener('click', () => this.exitFullscreen());

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (!this.isFullscreen) return;

      switch(e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          this.previousSlide();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
        case 'Enter':
          e.preventDefault();
          this.nextSlide();
          break;
        case 'Escape':
          e.preventDefault();
          this.exitFullscreen();
          break;
        case 'Home':
          e.preventDefault();
          this.goToSlide(0);
          break;
        case 'End':
          e.preventDefault();
          this.goToSlide(this.slides.length - 1);
          break;
      }
    });

    // Handle fullscreen change events
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement && this.isFullscreen) {
        this.exitFullscreen();
      }
    });
  }

  async enterFullscreen() {
    this.isFullscreen = true;
    this.fullscreenContainer.classList.remove('hidden');

    // Try to enter browser fullscreen mode
    try {
      await this.fullscreenContainer.requestFullscreen();
    } catch (err) {
      console.log('Fullscreen not supported, using full-window mode');
    }

    // Render the first slide
    this.renderSlide(this.currentSlideIndex);
  }

  exitFullscreen() {
    this.isFullscreen = false;
    this.fullscreenContainer.classList.add('hidden');

    // Exit browser fullscreen if active
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  }

  nextSlide() {
    if (this.currentSlideIndex < this.slides.length - 1) {
      this.currentSlideIndex++;
      this.renderSlide(this.currentSlideIndex);
    }
  }

  previousSlide() {
    if (this.currentSlideIndex > 0) {
      this.currentSlideIndex--;
      this.renderSlide(this.currentSlideIndex);
    }
  }

  goToSlide(index) {
    if (index >= 0 && index < this.slides.length) {
      this.currentSlideIndex = index;
      this.renderSlide(this.currentSlideIndex);
    }
  }

  renderSlide(index) {
    const slide = this.slides[index];
    const container = document.getElementById('slide-container');

    // Update slide counter
    document.getElementById('current-slide-number').textContent = index + 1;

    // Update button states
    const prevBtn = document.getElementById('prev-slide-btn');
    const nextBtn = document.getElementById('next-slide-btn');

    if (prevBtn) {
      prevBtn.disabled = index === 0;
      prevBtn.style.opacity = index === 0 ? '0.5' : '1';
    }

    if (nextBtn) {
      nextBtn.disabled = index === this.slides.length - 1;
      nextBtn.style.opacity = index === this.slides.length - 1 ? '0.5' : '1';
    }

    // Render the appropriate slide layout
    let slideHTML = '';

    switch(slide.contentType) {
      case 'cover':
        slideHTML = this.renderCoverSlide(slide);
        break;
      case 'goals_visual':
        slideHTML = this.renderGoalsSlide(slide);
        break;
      case 'insurance_comparison':
        slideHTML = this.renderInsuranceSlide(slide);
        break;
      case 'retirement_projection':
        slideHTML = this.renderRetirementSlide(slide);
        break;
      case 'tax_savings':
        slideHTML = this.renderTaxSavingsSlide(slide);
        break;
      case 'timeline':
        slideHTML = this.renderTimelineSlide(slide);
        break;
      default:
        slideHTML = '<div class="flex items-center justify-center h-full"><p class="text-white text-2xl">Slide not found</p></div>';
    }

    // Add fade transition
    container.style.opacity = '0';
    setTimeout(() => {
      container.innerHTML = slideHTML;
      container.style.transition = 'opacity 0.3s ease-in-out';
      container.style.opacity = '1';

      // Initialize animations after slide is rendered
      this.initializeSlideAnimations();
    }, 150);
  }

  initializeSlideAnimations() {
    // Animate numbers (count up effect)
    const animatedNumbers = document.querySelectorAll('.animate-number');
    animatedNumbers.forEach(element => {
      const target = parseInt(element.dataset.target.replace(/[^0-9]/g, ''));
      const prefix = element.dataset.prefix || '';
      const suffix = element.dataset.suffix || '';
      const duration = 1500;
      const start = 0;
      const increment = target / (duration / 16);
      let current = start;

      const updateNumber = () => {
        current += increment;
        if (current < target) {
          element.textContent = prefix + Math.floor(current).toLocaleString() + suffix;
          requestAnimationFrame(updateNumber);
        } else {
          element.textContent = prefix + target.toLocaleString() + suffix;
        }
      };

      updateNumber();
    });

    // Animate progress bars
    const progressBars = document.querySelectorAll('.animate-progress');
    progressBars.forEach(bar => {
      const width = bar.dataset.width;
      bar.style.width = '0%';
      setTimeout(() => {
        bar.style.transition = 'width 1s ease-out';
        bar.style.width = width;
      }, 100);
    });
  }

  renderCoverSlide(slide) {
    return `
      <div class="w-full h-full bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 flex items-center justify-center p-16 relative overflow-hidden">
        <!-- Decorative background pattern -->
        <div class="absolute inset-0 opacity-10">
          <svg class="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" stroke-width="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <!-- Content -->
        <div class="text-center text-white max-w-5xl relative z-10">
          <div class="mb-8">
            <svg class="w-24 h-24 mx-auto mb-6 text-white opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
            </svg>
          </div>
          <h1 class="text-8xl font-bold mb-6 leading-tight tracking-tight">${slide.title}</h1>
          <h2 class="text-5xl mb-12 font-light opacity-95">${slide.subtitle}</h2>
          <p class="text-3xl opacity-90 font-light">${slide.content.text || ''}</p>
        </div>

        <!-- Footer -->
        <div class="absolute bottom-0 left-0 right-0 px-16 py-6 text-white opacity-60">
          <p class="text-sm">Advice Innovation Hub</p>
        </div>
      </div>
    `;
  }

  renderSlideFooter(slideNumber) {
    return `
      <div class="absolute bottom-0 left-0 right-0 px-16 py-4 text-gray-500 text-sm border-t border-gray-200 bg-gray-50">
        <p>Advice Innovation Hub</p>
      </div>
    `;
  }

  renderGoalsSlide(slide) {
    // Icon mapping for different goal types
    const getGoalIcon = (goal) => {
      const iconText = goal.icon || goal.goal.toLowerCase();

      if (iconText.includes('retire') || iconText.includes('palm')) {
        return `<svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>`;
      } else if (iconText.includes('protect') || iconText.includes('shield')) {
        return `<svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
        </svg>`;
      } else if (iconText.includes('education') || iconText.includes('graduation')) {
        return `<svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5z"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"></path>
        </svg>`;
      } else if (iconText.includes('property') || iconText.includes('home')) {
        return `<svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
        </svg>`;
      }
      // Default icon
      return `<svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>`;
    };

    const goalsHTML = slide.content.map((goal, index) => `
      <div class="bg-white border border-gray-200 rounded-lg p-6" style="animation: slideIn 0.5s ease-out ${index * 0.1}s both">
        <div class="flex items-start">
          <div class="flex-shrink-0 w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-blue-600">
            ${getGoalIcon(goal)}
          </div>
          <div class="flex-1">
            <p class="font-semibold text-gray-900 mb-2 text-2xl">${goal.goal}</p>
            <p class="text-lg text-gray-600">${goal.description}</p>
          </div>
        </div>
      </div>
    `).join('');

    return `
      <style>
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      </style>
      <div class="w-full h-full bg-gray-50 p-16 pb-20 relative">
        <!-- Header Section (Top Left) -->
        <div class="mb-8">
          <h2 class="text-6xl font-bold text-gray-900 mb-3">${slide.title}</h2>
          <p class="text-3xl text-gray-600">${slide.subtitle}</p>
        </div>

        <!-- Content Grid -->
        <div class="grid grid-cols-2 gap-6">
          ${goalsHTML}
        </div>

        ${this.renderSlideFooter(slide.number || 2)}
      </div>
    `;
  }

  renderInsuranceSlide(slide) {
    return `
      <div class="w-full h-full bg-gray-50 p-16 pb-20 relative">
        <!-- Header Section (Top Left) -->
        <div class="mb-8">
          <h2 class="text-6xl font-bold text-gray-900 mb-3">${slide.title}</h2>
          <p class="text-3xl text-gray-600">${slide.subtitle}</p>
        </div>

        <!-- Alert Card -->
        <div class="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-8 flex items-center">
          <svg class="w-6 h-6 text-amber-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
          <p class="text-xl text-amber-900">${slide.content.message}</p>
        </div>

        <!-- Comparison Grid -->
        <div class="grid grid-cols-2 gap-8 mb-8">
          <!-- Current Coverage Card -->
          <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 class="text-2xl font-semibold text-gray-900">Current Coverage</h3>
            </div>
            <div class="px-6 py-6 space-y-4">
              <div class="border-l-4 border-gray-400 pl-4">
                <p class="text-lg font-semibold text-gray-900 mb-2">James</p>
                <p class="text-lg text-gray-700">Life: ${slide.content.current.james.life}</p>
                <p class="text-lg text-gray-700">IP: ${slide.content.current.james.ip}</p>
              </div>
              <div class="border-l-4 border-gray-400 pl-4">
                <p class="text-lg font-semibold text-gray-900 mb-2">Sarah</p>
                <p class="text-lg text-gray-700">Life: ${slide.content.current.sarah.life}</p>
              </div>
            </div>
          </div>

          <!-- Recommended Coverage Card -->
          <div class="bg-white border border-blue-200 rounded-lg overflow-hidden">
            <div class="px-6 py-4 border-b border-blue-200 bg-blue-50">
              <h3 class="text-2xl font-semibold text-blue-900">Recommended</h3>
            </div>
            <div class="px-6 py-6 space-y-4">
              <div class="border-l-4 border-blue-500 pl-4">
                <p class="text-lg font-semibold text-gray-900 mb-2">James</p>
                <p class="text-lg text-gray-700">Life: ${slide.content.recommended.james.life}</p>
                <p class="text-lg text-gray-700">IP: ${slide.content.recommended.james.ip}</p>
              </div>
              <div class="border-l-4 border-blue-500 pl-4">
                <p class="text-lg font-semibold text-gray-900 mb-2">Sarah</p>
                <p class="text-lg text-gray-700">Life: ${slide.content.recommended.sarah.life}</p>
                <p class="text-lg text-gray-700">IP: ${slide.content.recommended.sarah.ip}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Investment Card -->
        <div class="bg-white border border-gray-200 rounded-lg p-6 flex items-center justify-between">
          <div class="flex items-center">
            <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <span class="text-2xl font-semibold text-gray-900">Investment Required:</span>
          </div>
          <span class="text-3xl font-bold text-blue-900">${slide.content.cost}</span>
        </div>

        ${this.renderSlideFooter(slide.number || 3)}
      </div>
    `;
  }

  renderRetirementSlide(slide) {
    // Extract numeric values for chart
    const currentAmount = parseInt(slide.content.current_trajectory.income.replace(/[^0-9]/g, ''));
    const recommendedAmount = parseInt(slide.content.with_recommendations.income.replace(/[^0-9]/g, ''));
    const maxAmount = Math.max(currentAmount, recommendedAmount);
    const currentPercent = (currentAmount / maxAmount) * 100;
    const recommendedPercent = (recommendedAmount / maxAmount) * 100;

    return `
      <div class="w-full h-full bg-gray-50 p-16 pb-20 relative">
        <!-- Header Section (Top Left) -->
        <div class="mb-8">
          <h2 class="text-6xl font-bold text-gray-900 mb-3">${slide.title}</h2>
          <p class="text-3xl text-gray-600">${slide.subtitle}</p>
        </div>

        <!-- Success Message Card -->
        <div class="bg-green-50 border border-green-200 rounded-lg p-5 mb-8 flex items-center">
          <svg class="w-6 h-6 text-green-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="text-xl text-green-900">${slide.content.message}</p>
        </div>

        <!-- Visual Bar Chart -->
        <div class="bg-white border border-gray-200 rounded-lg p-8 mb-8">
          <div class="space-y-10">
            <!-- Current Trajectory Bar -->
            <div>
              <div class="flex justify-between items-baseline mb-4">
                <h3 class="text-2xl font-semibold text-gray-900">Current Trajectory</h3>
                <span class="text-4xl font-bold text-gray-900 animate-number" data-target="${currentAmount}" data-prefix="$" data-suffix="/year">$0/year</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-10 overflow-hidden">
                <div class="bg-gray-400 h-10 rounded-full animate-progress"
                     data-width="${currentPercent}%"
                     style="width: 0%">
                </div>
              </div>
            </div>

            <!-- Recommended Bar -->
            <div>
              <div class="flex justify-between items-baseline mb-4">
                <h3 class="text-2xl font-semibold text-gray-900">With Recommendations</h3>
                <span class="text-4xl font-bold text-green-900 animate-number" data-target="${recommendedAmount}" data-prefix="$" data-suffix="/year">$0/year</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-10 overflow-hidden">
                <div class="bg-green-500 h-10 rounded-full animate-progress"
                     data-width="${recommendedPercent}%"
                     style="width: 0%">
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Boost Callout Card -->
        <div class="bg-green-50 border border-green-200 rounded-lg p-8 flex items-center justify-between">
          <span class="text-2xl font-semibold text-gray-900">Additional Annual Income:</span>
          <span class="text-5xl font-bold text-green-900">${slide.content.boost}</span>
        </div>

        ${this.renderSlideFooter(slide.number || 4)}
      </div>
    `;
  }

  renderTaxSavingsSlide(slide) {
    // Calculate max savings for progress bar scaling
    const savingsAmounts = slide.content.savings.map(s => parseInt(s.amount.replace(/[^0-9]/g, '')));
    const maxSaving = Math.max(...savingsAmounts);
    const totalSavings = parseInt(slide.content.total_annual_savings.replace(/[^0-9]/g, ''));

    const savingsHTML = slide.content.savings.map((saving, index) => {
      const amount = parseInt(saving.amount.replace(/[^0-9]/g, ''));
      const percentage = (amount / maxSaving) * 100;

      return `
      <div class="mb-6" style="animation: slideIn 0.5s ease-out ${index * 0.15}s both">
        <div class="flex items-center justify-between mb-3">
          <span class="text-xl font-medium text-gray-900">${saving.strategy}</span>
          <span class="text-2xl font-bold text-blue-900 animate-number" data-target="${amount}" data-prefix="$" data-suffix="/year">$0/year</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-8 overflow-hidden">
          <div class="bg-blue-500 h-8 rounded-full animate-progress"
               data-width="${percentage}%"
               style="width: 0%">
          </div>
        </div>
      </div>
    `}).join('');

    return `
      <style>
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      </style>
      <div class="w-full h-full bg-gray-50 p-16 pb-20 relative">
        <!-- Header Section (Top Left) -->
        <div class="mb-8">
          <h2 class="text-6xl font-bold text-gray-900 mb-3">${slide.title}</h2>
          <p class="text-3xl text-gray-600">${slide.subtitle}</p>
        </div>

        <!-- Info Message Card -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-8 flex items-center">
          <svg class="w-6 h-6 text-blue-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="text-xl text-blue-900">${slide.content.message}</p>
        </div>

        <!-- Savings Breakdown Card -->
        <div class="bg-white border border-gray-200 rounded-lg p-8 mb-8">
          ${savingsHTML}
        </div>

        <!-- Total Card -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-10 text-center">
          <p class="text-2xl text-gray-900 mb-3 font-semibold">Total Annual Savings</p>
          <p class="text-7xl font-bold text-blue-900 mb-4 animate-number" data-target="${totalSavings}" data-prefix="$" data-suffix="/year">$0/year</p>
          <div class="inline-block bg-white border border-blue-200 rounded-lg px-6 py-3">
            <p class="text-xl text-blue-900">${slide.content.cumulative_10_years}</p>
          </div>
        </div>

        ${this.renderSlideFooter(slide.number || 5)}
      </div>
    `;
  }

  renderTimelineSlide(slide) {
    // Color scheme for different timeline phases - using consistent blue theme
    const phaseLabels = ['Immediate', 'Short-term', 'Medium-term', 'Long-term'];

    const phasesHTML = slide.content.map((phase, index) => {
      return `
      <div class="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6" style="animation: slideIn 0.5s ease-out ${index * 0.15}s both">
        <div class="px-6 py-4 border-b border-gray-200 bg-blue-50">
          <h3 class="text-2xl font-semibold text-blue-900">${phase.phase}</h3>
        </div>
        <div class="px-6 py-6">
          <ul class="space-y-3">
            ${phase.actions.map(action => `
              <li class="flex items-start text-lg text-gray-700">
                <svg class="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span>${action}</span>
              </li>
            `).join('')}
          </ul>
        </div>
      </div>
    `}).join('');

    return `
      <style>
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      </style>
      <div class="w-full h-full bg-gray-50 p-16 pb-20 relative">
        <!-- Header Section (Top Left) -->
        <div class="mb-8">
          <h2 class="text-6xl font-bold text-gray-900 mb-3">${slide.title}</h2>
          <p class="text-3xl text-gray-600">${slide.subtitle}</p>
        </div>

        <!-- Timeline Cards -->
        <div>
          ${phasesHTML}
        </div>

        ${this.renderSlideFooter(slide.number || 6)}
      </div>
    `;
  }
}

// Initialize presentation controller
function initializePresentationController() {
  // Only initialize if we're on a presentation page AND not the Infinity FC case study
  const hasSlides = document.querySelector('[data-slide]');
  const isInfinityFcCaseStudy = document.body.getAttribute('data-presentation-type') === 'infinity-fc-case-study';

  if (hasSlides && !isInfinityFcCaseStudy) {
    window.presentationController = new PresentationController();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initializePresentationController);

// Also initialize on Turbo navigation
document.addEventListener('turbo:load', initializePresentationController);
